import React, { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import type { TariffRateItem, TariffStatus } from '../../../apis/types'
import { createTariffRateApi } from '../../../apis/apis'
import { FieldTooltip, PCIA_YEAR_HINT } from './TariffFieldKey'
import { lockedRateWindow } from './tariffHistoryUtils'

const RATE_TYPES = ['CHARGE', 'ADJUSTMENT', 'MAX'] as const
const RATE_CATEGORIES = ['TOU', 'DEMAND', 'NBC', 'NON_TOU_NON_NBC', 'CUSTOMER'] as const
const RATE_GROUPS = [
    'GENERATION',
    'DELIVERY',
    'DEMAND',
    'NBC',
    'BUNDLED_FEE',
    'PDP',
    'SERVICE',
    'NONE',
] as const

const SEASONS = ['SUMMER', 'WINTER', 'ALL'] as const
const TOU_PERIODS = ['PEAK', 'MID_PEAK', 'PARTIAL_PEAK', 'OFF_PEAK', 'SUPER_OFF_PEAK', 'ALL'] as const
const RATE_PERIOD_TYPES = ['DAILY', 'MONTHLY', 'ANNUAL'] as const

const inputClass =
    'block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-indigo-600 focus:ring-indigo-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

/** Accepts optional sign, integer or decimal (no scientific notation / trailing junk). */
const DECIMAL_PATTERN = /^-?(?:\d+\.?\d*|\.\d+)$/

type FieldErrors = {
    name?: string
    rate?: string
    rateEffectiveDate?: string
    reason?: string
}

export const CreateTariffRateForm: React.FC<{
    tariffCuid: string
    tariffHistoryCuid: string
    historyEffectiveDate?: string | null
    historyEndDate?: string | null
    historyStatus?: TariffStatus
    siblingEndDates?: Array<string | null | undefined>
    onCreated: (rate: TariffRateItem) => void
    /** Optional class on the Add rate trigger button. */
    buttonClassName?: string
}> = ({
    tariffCuid,
    tariffHistoryCuid,
    historyEffectiveDate,
    historyEndDate,
    historyStatus,
    siblingEndDates,
    onCreated,
    buttonClassName,
}) => {
    const [open, setOpen] = useState(false)
    const [name, setName] = useState('')
    const [rate, setRate] = useState('')
    const [type, setType] = useState<string>('CHARGE')
    const [category, setCategory] = useState<string>('TOU')
    const [rateGroup, setRateGroup] = useState<string>('GENERATION')
    const [season, setSeason] = useState('')
    const [touPeriod, setTouPeriod] = useState('')
    const [ratePeriodType, setRatePeriodType] = useState('')
    const windowDates = lockedRateWindow({
        historyEffectiveDate,
        historyEndDate,
        historyStatus,
        siblingEndDates,
    })
    const [vintageYear, setVintageYear] = useState('')
    const [coincidental, setCoincidental] = useState(false)
    const [modifiedDescription, setModifiedDescription] = useState('')
    const [reason, setReason] = useState('')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

    const reset = (): void => {
        setName('')
        setRate('')
        setType('CHARGE')
        setCategory('TOU')
        setRateGroup('GENERATION')
        setSeason('')
        setTouPeriod('')
        setRatePeriodType('')
        setVintageYear('')
        setCoincidental(false)
        setModifiedDescription('')
        setReason('')
        setError(null)
        setFieldErrors({})
    }

    const close = (): void => {
        reset()
        setOpen(false)
    }

    const validate = (): { ok: true; parsedRate: number } | { ok: false } => {
        const next: FieldErrors = {}
        if (name.trim() === '') next.name = 'Name is required.'
        const trimmedRate = rate.trim()
        if (trimmedRate === '') {
            next.rate = 'Rate is required.'
        } else if (!DECIMAL_PATTERN.test(trimmedRate)) {
            next.rate = 'Enter a valid decimal number.'
        }
        if (windowDates.effectiveDate === '') next.rateEffectiveDate = 'Effective date is required.'
        if (reason.trim() === '') next.reason = 'Reason is required.'
        setFieldErrors(next)
        if (Object.keys(next).length > 0) return { ok: false }
        return { ok: true, parsedRate: Number(trimmedRate) }
    }

    const handleCreate = (): void => {
        const result = validate()
        if (!result.ok) return
        setSaving(true)
        setError(null)
        void createTariffRateApi(tariffCuid, {
            name: name.trim(),
            rate: result.parsedRate,
            type,
            category,
            rateGroup,
            season: season === '' ? null : season,
            touPeriod: touPeriod === '' ? null : touPeriod,
            ratePeriodType: ratePeriodType === '' ? null : ratePeriodType,
            coincidental,
            rateEffectiveDate: windowDates.effectiveDate,
            rateEndDate: windowDates.endDate,
            vintageYear: vintageYear.trim() === '' ? null : vintageYear.trim(),
            modifiedDescription:
                modifiedDescription.trim() === '' ? undefined : modifiedDescription.trim(),
            reason: reason.trim(),
            tariffHistoryCuid,
        })
            .then((created) => {
                onCreated(created)
                close()
            })
            .catch(() => setError('Failed to create rate.'))
            .finally(() => setSaving(false))
    }

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className={
                    buttonClassName ??
                    'rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500'
                }
            >
                Add rate
            </button>

            <Transition show={open} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={close}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/50 dark:bg-black/70" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 translate-y-4 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-2xl rounded-lg bg-white p-5 text-left shadow-xl dark:bg-slate-900">
                                    <Dialog.Title className="text-lg font-medium text-slate-900 dark:text-slate-100">
                                        Add rate
                                    </Dialog.Title>
                                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                            Name
                                            <input
                                                className={`${inputClass} mt-1`}
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                            />
                                            {fieldErrors.name != null ? (
                                                <span className="mt-1 block text-xs text-red-600">
                                                    {fieldErrors.name}
                                                </span>
                                            ) : null}
                                        </label>
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                            Rate
                                            <input
                                                className={`${inputClass} mt-1`}
                                                inputMode="decimal"
                                                value={rate}
                                                onChange={(e) => setRate(e.target.value)}
                                                placeholder="0.0000"
                                            />
                                            {fieldErrors.rate != null ? (
                                                <span className="mt-1 block text-xs text-red-600">
                                                    {fieldErrors.rate}
                                                </span>
                                            ) : null}
                                        </label>
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                            Type
                                            <select
                                                className={`${inputClass} mt-1`}
                                                value={type}
                                                onChange={(e) => setType(e.target.value)}
                                            >
                                                {RATE_TYPES.map((v) => (
                                                    <option key={v} value={v}>
                                                        {v}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                            Category
                                            <select
                                                className={`${inputClass} mt-1`}
                                                value={category}
                                                onChange={(e) => setCategory(e.target.value)}
                                            >
                                                {RATE_CATEGORIES.map((v) => (
                                                    <option key={v} value={v}>
                                                        {v}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                            Rate group
                                            <select
                                                className={`${inputClass} mt-1`}
                                                value={rateGroup}
                                                onChange={(e) => setRateGroup(e.target.value)}
                                            >
                                                {RATE_GROUPS.map((v) => (
                                                    <option key={v} value={v}>
                                                        {v}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                            Season
                                            <select
                                                className={`${inputClass} mt-1`}
                                                value={season}
                                                onChange={(e) => setSeason(e.target.value)}
                                            >
                                                <option value="">—</option>
                                                {SEASONS.map((v) => (
                                                    <option key={v} value={v}>
                                                        {v}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                            TOU period
                                            <select
                                                className={`${inputClass} mt-1`}
                                                value={touPeriod}
                                                onChange={(e) => setTouPeriod(e.target.value)}
                                            >
                                                <option value="">—</option>
                                                {TOU_PERIODS.map((v) => (
                                                    <option key={v} value={v}>
                                                        {v}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                            Rate period type
                                            <select
                                                className={`${inputClass} mt-1`}
                                                value={ratePeriodType}
                                                onChange={(e) => setRatePeriodType(e.target.value)}
                                            >
                                                <option value="">—</option>
                                                {RATE_PERIOD_TYPES.map((v) => (
                                                    <option key={v} value={v}>
                                                        {v}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <div className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                            Effective date
                                            <p className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                                                {windowDates.effectiveDate !== ''
                                                    ? windowDates.effectiveDate
                                                    : '—'}
                                            </p>
                                            {fieldErrors.rateEffectiveDate != null ? (
                                                <span className="mt-1 block text-xs text-red-600">
                                                    {fieldErrors.rateEffectiveDate}
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                            End date
                                            <p className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                                                {windowDates.endLabel}
                                            </p>
                                        </div>
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                            <FieldTooltip content={PCIA_YEAR_HINT}>PCIA year</FieldTooltip>
                                            <input
                                                className={`${inputClass} mt-1`}
                                                value={vintageYear}
                                                onChange={(e) => setVintageYear(e.target.value)}
                                            />
                                        </label>
                                        <label className="flex items-center gap-2 self-end text-xs font-medium text-slate-600 dark:text-slate-300">
                                            <input
                                                type="checkbox"
                                                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-600"
                                                checked={coincidental}
                                                onChange={(e) => setCoincidental(e.target.checked)}
                                            />
                                            Coincidental
                                        </label>
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 sm:col-span-2">
                                            Notes
                                            <textarea
                                                className={`${inputClass} mt-1`}
                                                rows={2}
                                                value={modifiedDescription}
                                                onChange={(e) => setModifiedDescription(e.target.value)}
                                            />
                                        </label>
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 sm:col-span-2">
                                            Reason{' '}
                                            <span className="font-normal text-slate-500">(required)</span>
                                            <textarea
                                                className={`${inputClass} mt-1`}
                                                rows={2}
                                                maxLength={500}
                                                value={reason}
                                                onChange={(e) => setReason(e.target.value)}
                                            />
                                            {fieldErrors.reason != null ? (
                                                <span className="mt-1 block text-xs text-red-600">
                                                    {fieldErrors.reason}
                                                </span>
                                            ) : null}
                                        </label>
                                    </div>
                                    {error != null ? (
                                        <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
                                    ) : null}
                                    <div className="mt-5 flex justify-end gap-2">
                                        <button
                                            type="button"
                                            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                                            onClick={close}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60"
                                            onClick={handleCreate}
                                            disabled={saving || reason.trim() === ''}
                                        >
                                            {saving ? 'Saving…' : 'Save rate'}
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    )
}

export default CreateTariffRateForm
